import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useCategories, useMarkets, useMe } from "../api/hooks";
import type { MarketStatus } from "../api/types";
import { MarketCard } from "../components/market";
import { Empty, Input, QueryState, cx } from "../components/ui";

const TABS: { status: MarketStatus; label: string }[] = [
  { status: "OPEN", label: "Open" },
  { status: "VOTING", label: "Voting" },
  { status: "RESOLVED", label: "Resolved" },
  { status: "FINAL", label: "Final" },
];

export function MarketsPage() {
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as MarketStatus) || "OPEN";
  const category = params.get("category") ?? "";
  const [search, setSearch] = useState("");
  const cats = useCategories();
  const markets = useMarkets(status, category);
  const me = useMe();

  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  // Title search is client-side over the loaded list (spec: a few hundred markets at most).
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (markets.data ?? []).filter((m) => !q || m.title.toLowerCase().includes(q));
  }, [markets.data, search]);

  const pill = (active: boolean) =>
    cx("shrink-0 rounded-full px-3 py-1.5 text-sm", active ? "bg-fg text-bg" : "bg-surface-2 text-muted hover:text-fg");

  return (
    <div className="space-y-4">
      {me.data && (me.data.votableCount ?? 0) > 0 && status !== "VOTING" && (
        <button onClick={() => set("status", "VOTING")} className="w-full rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-left text-sm">
          <strong>{me.data.votableCount}</strong> market{me.data.votableCount === 1 ? " needs" : "s need"} your vote →
        </button>
      )}

      <div className="flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.status} className={pill(status === t.status)} onClick={() => set("status", t.status === "OPEN" ? "" : t.status)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto">
        <button className={pill(!category)} onClick={() => set("category", "")}>All</button>
        {cats.data?.map((c) => (
          <button key={c.slug} className={pill(category === c.slug)} onClick={() => set("category", c.slug)}>{c.name}</button>
        ))}
      </div>

      <Input type="search" placeholder="Search markets" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search markets" />

      <QueryState q={markets}>
        {() =>
          shown.length === 0 ? (
            <Empty>
              {search ? "No markets match your search." : "No markets here yet."}{" "}
              {status === "OPEN" && me.data?.user.role === "member" && <Link to="/new" className="text-accent">Create one</Link>}
            </Empty>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {shown.map((m) => <MarketCard key={m.id} market={m} />)}
            </div>
          )
        }
      </QueryState>
    </div>
  );
}
